import axios from "axios"
import qs from "query-string"
import { useLocalQueryService, TIMEPERIOD, DEFAULT_TIME_PERIOD, URL } from 'Constants'
import { store } from 'store/store';
import { UPDATE_MESSAGE} from 'store/messageStore'
import { UPDATE_SESSION_STORAGE } from 'store/UserSession'
import { getUpdatedDate } from 'helpers/constants_helper'
import { Event } from 'components/Event'

// for direct connection through sshuttle ... click chrome shield and allow insecure

// These are internal state.  Nothing else interacts with them except through initAxios.
let refreshAccessTokenRequest;
let authenticatingInterceptor;
let sessionId;
let accessToken;
let defaultMaxRefresh = 5;
let debouncer = {activeQueries: 0, attemptedQueries: 0, awaitingRefresh: false, maxRefresh: defaultMaxRefresh};
let debouncerTimeout = 60000;

const getAPIParams = (RESOURCE,search,params) => {
  
  if (!params) {
    params = {};
  }
  if (!params.limit) {
    params.limit = 100;
  }
  let searchParams = qs.parse(search);
  Object.keys(searchParams).forEach(key => {
    // strip out known UI parameters
    if (key !== "timePeriod" && key !== "dataSource") {
      params[key] = searchParams[key];
    }
  });
  let timeKey = DEFAULT_TIME_PERIOD;
  if (searchParams) {
    if (searchParams.timePeriod) {
      timeKey = searchParams.timePeriod.toUpperCase();
    }
  }

  let TIME = TIMEPERIOD[timeKey];
  if (!TIME) {
    console.log("There is no set of sources for timePeriod [%s]", timeKey);
    return false;
  }
  if (!params.startOnAfter) {
    let startOnAfter = getUpdatedDate(timeKey);
    params.startOnAfter = startOnAfter.toISOString();
  }
  if (!params.timeInterval) {
    params.timeInterval = TIMEPERIOD[timeKey].INTERVAL;
  }
  // strip out UI params
  return {params: params, RESOURCE: RESOURCE, startOnAfter: params.startOnAfter};
};

const clearDebouncerResource = (resource) => {
  let query = qs.stringify(resource.params);
  let key = resource.RESOURCE.KEY;
  if (debouncer[key] && debouncer[key].query === query) {
    debouncer.activeQueries--;
    if (debouncer.activeQueries === 0) {
      debouncer.attemptedQueries = 0;
    }
    delete debouncer[key];
  }
}

// return true if we debounce the request
const debounce = (resource,requestFinished=false) => {
  // debouncer structure:
  // debouncer {
  //   activeQueries: integer (0 when done accessing)
  //   attemptedQueries: integer (0 when latest block is done)
  //   resource: {
  //     query: source:queryString
  //     inProgress: boolean
  //     lastUpdated: ssep
  //   }
  // }

  let query = qs.stringify(resource.params);
  let key = resource.RESOURCE.KEY;
  let now = (new Date()).getTime();

  // finishing a request
  if (requestFinished) {
    if (debouncer[key] && debouncer[key].query === query) {
      debouncer[key].inProgress = false;
      debouncer[key].lastUpdated = now;
      debouncer.activeQueries--;
    } 
    if (debouncer.activeQueries === 0) {
      debouncer.attemptedQueries = 0;
    } 
    return false;
  }

  // adding a request
  if (!debouncer[key]) {
    debouncer[key] = {inProgress:true, lastUpdated:now, query:query};
    debouncer.activeQueries++;
    debouncer.attemptedQueries++;
    return false;
  }

  // different data source/filter on the same resource
  if (debouncer[key].query !== query) {
    debouncer[key] = {inProgress:true, lastUpdated:now, query:query}
    debouncer.activeQueries++;
    debouncer.attemptedQueries++;
    return false;
  }

  // in progress already
  if (debouncer[key].inProgress) {
    console.debug("Search for [%s] already in progress. Debouncing.",key);
    return true;
  }

  // too soon
  if (now - debouncer[key].lastUpdated < debouncerTimeout) {
    console.debug("Data for [%s] was already recently grabbed. Debouncing.",key);
    return true;
  } 

  // this is a repeat of a previous query, but after an acceptable time window
  debouncer[key] = {inProgress:true, lastUpdated:now, query:query};
  debouncer.activeQueries++;
  debouncer.attemptedQueries++;
  return false;
};

export const resetQuery = (RESOURCE,search,params) => {
  let resource = getAPIParams(RESOURCE,search,params);
  clearDebouncerResource(resource);
  store.dispatch({                                                               
    type: resource.RESOURCE.RESET                                           
  });   
};

export const dataApiRequest = axios.create({
  paramsSerializer: qs.stringify
});

export const getApiData = (RESOURCE,search,params) => {
  let resource = getAPIParams(RESOURCE,search,params);
  if (debounce(resource)) {
    return false;
  }
  let headers = Object.assign({},dataApiRequest.defaults.headers.common);

  if (useLocalQueryService && 
      resource.RESOURCE.URL.includes("localhost") &&
      !dataApiRequest.defaults.headers.common["x-plcm-tid"]) 
  {
    let tid = store.getState().userSession.tenantId;
    if (tid === "") {
      console.warn("No tenant ID yet");
    }
    headers["x-plcm-tid"] =
      store.getState().userSession.tenantId;
  } 
  if (useLocalQueryService && 
      resource.RESOURCE.URL.includes("localhost") && 
      resource.params["x-plcm-tid"]) 
  {
    let tid = resource.params["x-plcm-tid"];
    headers["x-plcm-tid"] = tid;
  }

  console.log("Query: [%s] %O",resource.RESOURCE.KEY,
      { URL: resource.RESOURCE.URL,
        Headers: headers,
        Parameters: resource.params}
  );

  store.dispatch({
    type: resource.RESOURCE.LOADING
  });

  dataApiRequest
    .get(resource.RESOURCE.URL, { params: resource.params, headers: headers })
    .then(response => {
      console.log("API Response: [%s] %O",resource.RESOURCE.KEY,response);
      if (response && response.data) {
        store.dispatch({
          type: resource.RESOURCE.KEY,
          data: response.data
        });
      }
    })
    .catch((error) => {
      clearDebouncerResource(resource);
      if (error && error.response) {
        if (error.response.data &&
          401 === error.response.data.http_status_code &&
          error.config &&
          error.config.__isRetryRequest)
        {
          // do nothing if it's a 401 and IS one we're already retrying...
        } else {
          console.log("Error response: %O",error.response);
          console.log("Error status: %O",error.response.status); 
          Event("Api Error",true,"RESP:"+JSON.stringify(error.response));
          store.dispatch({
            type: UPDATE_MESSAGE,
            message: { 
              doDisplay: true,
              header: "Status " + error.response.status,
              color: "red",
              text: error.response.data.description ? error.response.data.description : error.response.statusText
            }
          });
        }
      } else if (error.request) {
        console.log("Error message: %O", error);
        console.log("Error in request: %O", error.request);
        Event("Api Error",true,"REQ:"+JSON.stringify(error.request));
        store.dispatch({
          type: UPDATE_MESSAGE,
          message: { 
            doDisplay: true,
            header: "No reponse received",
            color: "red",
            text: "Check [" + resource.RESOURCE.URL + "]"
          }
        });
      } else {
        console.log("Unknown error: %O", error);
        Event("Api Error",true,"UNK:"+JSON.stringify(error));
        store.dispatch({
          type: UPDATE_MESSAGE,
          message: { 
            doDisplay: true,
            header: "Error in API Request",
            color: "red",
            text: "Unknown error in API."
          }
        });
      }
      debounce(resource,true);
    })
    .then(() => {
      debounce(resource,true);
    });
  return true; // not debounced 
}

export const orionApiRequest = axios.create({ baseURL: URL.ORION_API });
orionApiRequest.defaults.withCredentials = true;

export const isAuthenticated = () => sessionId && accessToken;

export const initAxios = (orionResponse, newsessionId) => {
  console.debug("Orion Auth Response: ",orionResponse);
  let newAccessToken = orionResponse.access_token;
  // If we're already initialized, don't do anything
  if (accessToken && sessionId) {
    console.warn("Attempted to reinitailize Axios");
    return;
  }

  dataApiRequest.interceptors.request.use(config => {
    for (const key of Object.keys(config.params)) {
      if ([null, undefined, ""].includes(config.params[key])) {
        delete config.params[key];
      }
    }
    // Add the Version specifier to the API URL
    const { version, ...params } = config.params;
    if (version) {
      console.warn(
        '"version" parameter supplied to API call.  This can cause problems, it will be ignored.'
      );
    }
    return { ...config, params: { version: 1, ...params } };
  });

  // Set the state variables that hold the access and refresh tokens
  sessionId = newsessionId;
  // Set the auth header
  setAuth(newAccessToken);
  doFutureRefresh(orionResponse.expires_in-10000);

  dataApiRequest.interceptors.response.eject(authenticatingInterceptor);
  authenticatingInterceptor = dataApiRequest.interceptors.response.use(
    // Nothing on success
    null,
    // On error, check if it's a 401.
    error => {
      // Make sure all properties of the response object exist - if not, rethrow
      if (!(error && error.response && error.response.data)) {
        throw error;
      }

      if (
        401 === error.response.data.http_status_code &&
        // If it's a 401 and ISN'T one we're already retrying...
        error.config &&
        !error.config.__isRetryRequest
      ) {
        // Try to refresh the access token and rerun the request.
        return refreshAccessToken(true).then(() => {
          error.config.__isRetryRequest = true;
          // Update the header for this request
          error.config.headers["Authorization"] = "BEARER " + accessToken;
          // And retry it
          return axios(error.config);
        })
      } else {
        console.log("API Error: %O",error.response.data);
        Event("Orion Error",true,"AUTH:"+JSON.stringify(error.response));
      }
      throw error;
    }
  );
};

// can throw an error (throwing so promise can catch it later)
export const logout = () => {
  setAuth(null);
  return axios
    .post(
      URL.AUTHLOGOUT,
      {},
      {
        withCredentials: true,
        header: {
          accept: "json"
        }
      }
    )
    .then(res => {
      console.log("Session Removed", res);
      sessionId = null;
    })
};

const setAuth = newToken => {
  if (newToken) {
    accessToken = newToken;
    dataApiRequest.defaults.headers.common["Authorization"] = "BEARER " + newToken;
  } else {
    dataApiRequest.defaults.headers.common["Authorization"] = "";
  }
};

const doFutureRefresh = (ms) => {
  if (!debouncer.awaitingRefresh) {
    debouncer.awaitingRefresh = true;
    setTimeout(() => {
      console.debug("Orion auth timeout");
    },ms-2500);
    setTimeout(() => {
      debouncer.maxRefresh--;
      refreshAccessToken();
      debouncer.awaitingRefresh = false;
    },ms-2000);
  }
};

const refreshAccessToken = (manual = false) => {
  // If there's an outstanding refresh request, just return that one.
  if (refreshAccessTokenRequest) {
    return refreshAccessTokenRequest;
  }
  if (debouncer.maxRefresh <= 0 && !manual) {
    console.debug("Max auto-refresh time exceeded.");
    return null;
  }
  if (manual) {
    debouncer.maxRefresh = defaultMaxRefresh;
  }

  // If not, send off for a new Access Token.
  console.debug("Sending OAuth refresh request");
  refreshAccessTokenRequest = axios
    .get(URL.OAUTH_REFRESH, {
      withCredentials: true,
      params: {
        SessionId: sessionId
      },
      header: {
        accept: "json"
      }
    })
    .then(res => {
      if (res.data && res.data.OrionResponse && res.data.OrionResponse.expires_in) {
        doFutureRefresh(res.data.OrionResponse.expires_in-10000);
      }
      // Update the state and clear the outstanding request
      let { 
        OrionResponse: { access_token },
        SessionId
      } = res.data;

      setAuth(access_token);
      sessionId = SessionId;
      return res;
    })
    .catch(error => {
      console.error("Error occurred while refreshing access token", error);
      Event("Orion Error",true,"REFRESH:"+JSON.stringify(error));
      refreshRejected();
    })
    .finally(() => {
      refreshAccessTokenRequest = null;
    });
  return refreshAccessTokenRequest;
};

const refreshRejected = () => {
  store.dispatch({
    type: UPDATE_SESSION_STORAGE,
    tenantId: store.getState().userSession.tenantId,
    userId: "",
    userName: "",
    rawUser: {}
  });
  store.dispatch({
    type: UPDATE_MESSAGE,
    message: { 
      doDisplay: true,
      header: "Authorization Expired",
      color: "red",
      text: "Please log in again to continue."
    }
  });
}
